import {
    Briefcase,
    Building2,
    CheckCircle,
    ClipboardCheck,
    Clock,
    DollarSign,
    Heart,
    MapPin,
    Star,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { uuidv7 } from 'uuidv7';
import { api, APIError } from '../api/client';
import { getSavedJobs } from '../utils/savedJobs';

export default function JobDetailsContent({ jobId, vacancyId: preferredVacancyId = null, jobTitle, vacancyDescription = null, onStartInterview, showStartButton = false }) {
    const [isSaved, setIsSaved] = useState(false);
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [parsing, setParsing] = useState(false);
    const [fullDescription, setFullDescription] = useState(vacancyDescription || '');
    const [fullHighlights, setFullHighlights] = useState(null);
    const [mobileOpenSection, setMobileOpenSection] = useState('about');
    const intervalRef = useRef(null);
    const isUuid = (value) =>
        typeof value === 'string' &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

    // Функция для проверки статуса парсинга
    // Returns: 'success' | 'error' | 'pending' | null
    const checkParsingStatus = async (vacancyId) => {
        try {
            console.log('[JobDetails] Checking parsing status for vacancy_id:', vacancyId);

            const data = await api.get(`/api/jobs_search_requests/parse/status/${vacancyId}`);
            console.log('[JobDetails] Parsing status response:', data);

            const status = typeof data.status === 'string' ? parseInt(data.status) : data.status;

            if (status === 2) {
                console.log('[JobDetails] Parsing is SUCCESS (2)');
                return 'success';
            }

            if (status === 3) {
                console.warn('[JobDetails] Parsing FAILED (3):', data.error_message);
                return 'error';
            }

            console.log('[JobDetails] Parsing status is', status, ', continue waiting...');
            return 'pending';
        } catch (err) {
            console.error('[JobDetails] Parsing status check error:', err);
            return null;
        }
    };

    // Функция для получения результата парсинга
    const fetchParsingResult = async (vacancyId) => {
        try {
            console.log('[JobDetails] Fetching parsing result for vacancy_id:', vacancyId);

            const data = await api.get(`/api/jobs_search_requests/parse/result/${vacancyId}`);
            console.log('[JobDetails] Parsing result:', data);

            if (data.full_description) {
                setFullDescription(data.full_description);
                console.log('[JobDetails] Full description loaded, length:', data.full_description.length);
            }

            if (data.highlights) {
                setFullHighlights(data.highlights);
                console.log('[JobDetails] Highlights loaded:', data.highlights);
            }
        } catch (err) {
            console.error('[JobDetails] Error fetching parsing result:', err);
        }
    };

    // Функция для проверки и загрузки существующего описания
    const checkExistingParsedData = async (vacancyId) => {
        try {
            console.log('[JobDetails] Checking for existing parsed data for vacancy_id:', vacancyId);

            const data = await api.get(`/api/jobs_search_requests/parse/result/${vacancyId}`);
            console.log('[JobDetails] Found existing parsed data:', data);

            if (data.full_description) {
                setFullDescription(data.full_description);
                console.log('[JobDetails] Loaded existing full description, length:', data.full_description.length);
            }

            if (data.highlights) {
                setFullHighlights(data.highlights);
                console.log('[JobDetails] Loaded existing highlights:', data.highlights);
            }

            return true; // Found existing data
        } catch (err) {
            if (err instanceof APIError && err.status === 404) {
                console.log('[JobDetails] No existing parsed data found (404), will need to parse');
                return false; // Need to parse
            }
            console.error('[JobDetails] Error checking existing parsed data:', err);
            return false; // On error, attempt to parse
        }
    };

    // Функция для запуска парсинга
    const startParsing = async (jobData) => {
        try {
            console.log('[JobDetails] Starting parsing process for job:', jobData);

            const requestId = localStorage.getItem('lastSearchRequestId');

            // Use vacancy_id from navigation/interview state when available.
            let vacancyId = isUuid(preferredVacancyId) ? preferredVacancyId : null;
            if (vacancyId) {
                console.log('[JobDetails] Using provided vacancy_id:', vacancyId);
            } else {
                try {
                    const storedVacancies = JSON.parse(localStorage.getItem('vacancyIdsByJob') || '{}');
                    if (storedVacancies[jobData.id]) {
                        vacancyId = storedVacancies[jobData.id];
                        console.log('[JobDetails] Reusing existing vacancy_id:', vacancyId);
                    }
                } catch (error) {
                    console.error('[JobDetails] Failed to read stored vacancies:', error);
                }
            }

            if (!vacancyId) {
                vacancyId = uuidv7();
                console.log('[JobDetails] Generated NEW vacancy_id:', vacancyId);
                try {
                    const storedVacancies = JSON.parse(localStorage.getItem('vacancyIdsByJob') || '{}');
                    storedVacancies[jobData.id] = vacancyId;
                    localStorage.setItem('vacancyIdsByJob', JSON.stringify(storedVacancies));
                } catch (error) {
                    console.error('[JobDetails] Failed to store vacancy_id:', error);
                }
            }

            // Сначала проверяем, есть ли уже распарсенные данные
            const hasExistingData = await checkExistingParsedData(vacancyId);
            if (hasExistingData) {
                console.log('[JobDetails] Using existing parsed data, skipping new parsing');
                return; // Уже есть данные, парсинг не нужен
            }

            if (!requestId || !jobData.job_id) {
                console.warn('[JobDetails] Skip parsing: missing request_id or job_id', {
                    requestId,
                    jobId: jobData.job_id,
                });
                return;
            }

            console.log('[JobDetails] No existing data found, starting new parsing...');
            setParsing(true);

            // Отправляем POST запрос на парсинг
            const parseData = await api.post('/api/jobs_search_requests/parse', {
                vacancy_id: vacancyId,
                request_id: requestId,
                job_id: jobData.job_id,
            });
            console.log('[JobDetails] Parse POST response:', parseData);

            // Запускаем проверку статуса каждые 5 секунд
            console.log('[JobDetails] Starting parsing status polling...');
            const statusCheckInterval = setInterval(async () => {
                const status = await checkParsingStatus(vacancyId);

                if (status === 'success') {
                    clearInterval(statusCheckInterval);
                    setParsing(false);

                    // Получаем результат парсинга
                    await fetchParsingResult(vacancyId);
                } else if (status === 'error') {
                    // Parsing failed, stop polling and try to load existing data
                    console.log('[JobDetails] Parsing failed, attempting to load existing data...');
                    clearInterval(statusCheckInterval);
                    setParsing(false);

                    // Try to fetch existing parsed result (might exist from previous successful parse)
                    await fetchParsingResult(vacancyId);
                }
                // If status === 'pending' or null, continue polling
            }, 5000);

            intervalRef.current = statusCheckInterval;

        } catch (err) {
            console.error('[JobDetails] Error starting parsing:', err);
            setParsing(false);
        }
    };

    // Очищаем интервал при размонтировании
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    // Загружаем данные вакансии при монтировании
    useEffect(() => {
        const loadJobData = async () => {
            try {
                console.log('[JobDetails] Loading job data for jobId:', jobId);

                // Сначала ищем в currentJobs
                const jobsData = localStorage.getItem('currentJobs');
                let foundJob = null;

                if (jobsData) {
                    try {
                        const jobs = JSON.parse(jobsData);
                        foundJob = jobs.find(j => j.id === jobId);
                        if (foundJob) {
                            console.log('[JobDetails] Found job in currentJobs:', foundJob);
                        }
                    } catch (parseError) {
                        console.error('[JobDetails] Error parsing currentJobs:', parseError);
                    }
                }

                // Если не найдено в currentJobs, ищем в savedJobs
                if (!foundJob) {
                    console.log('[JobDetails] Job not in currentJobs, checking savedJobs...');
                    try {
                        const savedJobs = getSavedJobs();
                        foundJob = savedJobs.find(j => String(j.id) === String(jobId));
                        if (foundJob) {
                            console.log('[JobDetails] Found job in savedJobs:', foundJob);
                        }
                    } catch (parseError) {
                        console.error('[JobDetails] Error parsing savedJobs:', parseError);
                    }
                }

                if (!foundJob) {
                    console.warn('[JobDetails] Job not found in localStorage:', jobId);

                    // Fallback: создаем базовый объект job из переданных данных
                    if (jobTitle) {
                        console.log('[JobDetails] Using fallback with provided jobTitle:', jobTitle);
                        foundJob = {
                            id: jobId,
                            title: jobTitle,
                            company: 'Company',
                            location: 'Location',
                            salary: 'Competitive',
                            description: 'Job description will be loaded...',
                            job_id: null,
                        };
                    } else {
                        console.error('[JobDetails] No job data available');
                        setLoading(false);
                        return;
                    }
                }

                console.log('[JobDetails] Job loaded successfully:', foundJob);
                setJob(foundJob);
                setLoading(false);

                // Автоматически запускаем парсинг при загрузке
                await startParsing(foundJob);
            } catch (err) {
                console.error('[JobDetails] Error loading job data:', err);
                setLoading(false);
            }
        };

        if (jobId) {
            loadJobData();
        }
    }, [jobId]);

    const handleToggleSave = () => {
        setIsSaved(!isSaved);
    };

    const splitDescriptionIntoParagraphs = (text) => {
        if (!text) {
            return [];
        }

        const normalized = text.replace(/\r\n/g, '\n').trim();
        if (!normalized) {
            return [];
        }

        const hasLineBreaks = normalized.includes('\n');
        let paragraphs = [];

        if (hasLineBreaks) {
            paragraphs = normalized
                .split(/\n\s*\n/)
                .map((paragraph) => paragraph.replace(/\n+/g, ' ').trim());
        } else {
            const sentences = normalized.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [normalized];
            const sentencesPerParagraph = 2;
            let buffer = [];

            sentences.forEach((sentence) => {
                const trimmed = sentence.trim();
                if (!trimmed) {
                    return;
                }
                buffer.push(trimmed);
                if (buffer.length >= sentencesPerParagraph) {
                    paragraphs.push(buffer.join(' '));
                    buffer = [];
                }
            });

            if (buffer.length) {
                paragraphs.push(buffer.join(' '));
            }
        }

        return paragraphs
            .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
            .filter(Boolean);
    };

    const renderDescription = (text) => {
        const paragraphs = splitDescriptionIntoParagraphs(text);
        if (!paragraphs.length) {
            return null;
        }

        return (
            <div className="space-y-3 text-gray-700 text-sm leading-relaxed">
                {paragraphs.map((paragraph, index) => (
                    <p key={`${index}-${paragraph.slice(0, 20)}`}>{paragraph}</p>
                ))}
            </div>
        );
    };

    const getWorkMode = (location = '') => {
        const lower = location.toLowerCase();
        if (lower.includes('remote')) return 'Remote';
        if (lower.includes('hybrid')) return 'Hybrid';
        return 'On-site';
    };

    const getTags = (title = '') => {
        const normalized = title.toLowerCase();
        const tags = [];
        if (normalized.includes('product')) tags.push('Product Management');
        if (normalized.includes('digital')) tags.push('Digital Strategy');
        if (normalized.includes('ux')) tags.push('UX');
        if (normalized.includes('agile')) tags.push('Agile');
        if (normalized.includes('analysis') || normalized.includes('analytics')) tags.push('Analytics');
        if (tags.length === 0) tags.push('General');
        return tags.slice(0, 5);
    };

    const normalizeHighlightList = (value) => {
        if (!Array.isArray(value)) {
            return [];
        }
        return value
            .map((item) => String(item || '').trim())
            .filter(Boolean);
    };

    const responsibilities = normalizeHighlightList(fullHighlights?.Responsibilities);
    const requirements = normalizeHighlightList(fullHighlights?.Qualifications);
    const benefits = normalizeHighlightList(fullHighlights?.Benefits);
    const showResponsibilities = parsing || responsibilities.length > 0;
    const showRequirements = parsing || requirements.length > 0;
    const showBenefits = parsing || benefits.length > 0;

    if (loading) {
        return (
            <div className="bg-white rounded-[24px] p-6 text-center text-gray-500">
                Loading job details...
            </div>
        );
    }

    if (!job) {
        return (
            <div className="bg-white rounded-[24px] p-6 text-center text-gray-500">
                Job not found
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-28">
            <div className="md:hidden space-y-3">
                <section className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-11 h-11 rounded-xl bg-orange-500 text-white flex items-center justify-center text-sm font-semibold">
                                {(job.company || job.title || 'J')[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-gray-500 truncate">{job.company || 'Company name'}</p>
                                <h1 className="text-lg font-semibold text-slate-900 leading-tight">{job.title}</h1>
                                <div className="mt-1 text-[11px] text-gray-500">{job.location || 'Location'}</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleToggleSave}
                            className={`h-9 w-9 rounded-lg border flex items-center justify-center ${isSaved ? 'border-orange-500 bg-orange-50 text-orange-500' : 'border-gray-200 text-gray-400'}`}
                            aria-label={isSaved ? 'Unsave job' : 'Save job'}
                        >
                            <Heart className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                        </button>
                    </div>

                    <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                        <span className="px-3 py-1 rounded-lg bg-[#F7F8FA] text-[11px] text-gray-700 whitespace-nowrap">Work: {getWorkMode(job.location)}</span>
                        <span className="px-3 py-1 rounded-lg bg-[#F7F8FA] text-[11px] text-gray-700 whitespace-nowrap">Salary: {job.salary || 'Competitive'}</span>
                        <span className="px-3 py-1 rounded-lg bg-[#F7F8FA] text-[11px] text-gray-700 whitespace-nowrap">Posted: 2 days ago</span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {getTags(job.title).slice(0, 3).map((tag) => (
                            <span key={`m-${job.id}-${tag}`} className="px-2.5 py-0.5 rounded-md border border-gray-200 text-[10px] text-gray-600">
                                {tag}
                            </span>
                        ))}
                    </div>
                </section>

                <section className="space-y-2">
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                        <button type="button" onClick={() => setMobileOpenSection((prev) => prev === 'about' ? '' : 'about')} className="w-full px-4 py-3 flex items-center justify-between">
                            <span className="text-sm font-semibold">About the Role</span>
                            <span className="text-orange-500 text-xs font-semibold">{mobileOpenSection === 'about' ? 'Hide' : 'Show'}</span>
                        </button>
                        {mobileOpenSection === 'about' && (
                            <div className="px-4 pb-4 text-sm text-gray-700">
                                {parsing ? <p className="text-gray-500 text-sm">Loading full description...</p> : (renderDescription(fullDescription) || renderDescription(job.description) || <p>This role combines strategic ownership with cross-functional collaboration.</p>)}
                            </div>
                        )}
                    </div>

                    {showResponsibilities && (
                        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                            <button type="button" onClick={() => setMobileOpenSection((prev) => prev === 'resp' ? '' : 'resp')} className="w-full px-4 py-3 flex items-center justify-between">
                                <span className="text-sm font-semibold">Responsibilities</span>
                                <span className="text-orange-500 text-xs font-semibold">{mobileOpenSection === 'resp' ? 'Hide' : 'Show'}</span>
                            </button>
                            {mobileOpenSection === 'resp' && (
                                <ul className="px-4 pb-4 space-y-2 text-sm text-gray-700">
                                    {parsing ? <li className="text-gray-500 text-sm">Loading full description...</li> : responsibilities.map((item) => (
                                        <li key={`m-r-${item}`} className="flex items-start gap-2">
                                            <span className="text-orange-500 mt-0.5">&#10003;</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {showRequirements && (
                        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                            <button type="button" onClick={() => setMobileOpenSection((prev) => prev === 'req' ? '' : 'req')} className="w-full px-4 py-3 flex items-center justify-between">
                                <span className="text-sm font-semibold">Requirements</span>
                                <span className="text-orange-500 text-xs font-semibold">{mobileOpenSection === 'req' ? 'Hide' : 'Show'}</span>
                            </button>
                            {mobileOpenSection === 'req' && (
                                <ul className="px-4 pb-4 space-y-2 text-sm text-gray-700">
                                    {parsing ? <li className="text-gray-500 text-sm">Loading full description...</li> : requirements.map((item) => (
                                        <li key={`m-q-${item}`} className="flex items-start gap-2">
                                            <span className="text-orange-500 mt-0.5">&#10003;</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {showBenefits && (
                        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                            <button type="button" onClick={() => setMobileOpenSection((prev) => prev === 'benefits' ? '' : 'benefits')} className="w-full px-4 py-3 flex items-center justify-between">
                                <span className="text-sm font-semibold">Benefits</span>
                                <span className="text-orange-500 text-xs font-semibold">{mobileOpenSection === 'benefits' ? 'Hide' : 'Show'}</span>
                            </button>
                            {mobileOpenSection === 'benefits' && (
                                <div className="px-4 pb-4 grid grid-cols-2 gap-2 text-xs text-gray-700">
                                    {parsing ? <p className="text-gray-500 text-sm col-span-2">Loading full description...</p> : benefits.map((item) => (
                                        <div key={`m-b-${item}`} className="rounded-lg bg-[#F7F8FA] px-3 py-2">{item}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </section>
                {showStartButton && (
                    <button
                        type="button"
                        onClick={onStartInterview}
                        className="w-full btn-orange py-3 rounded-xl text-sm font-semibold"
                    >
                        Start AI Interview
                    </button>
                )}
            </div>

            <div className="hidden md:block space-y-8">
            <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm space-y-6">
                <div className="flex items-start justify-between gap-6">
                    <div className="flex items-center gap-5">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-500 text-white flex items-center justify-center text-lg font-semibold">
                            {(job.company || job.title || 'J')[0].toUpperCase()}
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">{job.company || 'Company name'}</p>
                            <h1 className="text-2xl md:text-3xl font-semibold">{job.title}</h1>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleToggleSave}
                        className={`p-2 rounded-2xl border transition ${isSaved
                            ? 'border-red-200 bg-red-50 text-red-500'
                            : 'border-gray-200 text-gray-400 hover:text-gray-600'
                            }`}
                    >
                        <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
                    </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-[#F6F7FB] rounded-2xl p-4 flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-gray-400" />
                        <div>
                            <p className="text-xs text-gray-400">Location</p>
                            <p className="text-sm font-semibold">{job.location || 'Location'}</p>
                        </div>
                    </div>
                    <div className="bg-[#F6F7FB] rounded-2xl p-4 flex items-center gap-3">
                        <Briefcase className="w-5 h-5 text-gray-400" />
                        <div>
                            <p className="text-xs text-gray-400">Work Type</p>
                            <p className="text-sm font-semibold">{getWorkMode(job.location)}</p>
                        </div>
                    </div>
                    <div className="bg-[#F6F7FB] rounded-2xl p-4 flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-gray-400" />
                        <div>
                            <p className="text-xs text-gray-400">Salary</p>
                            <p className="text-sm font-semibold">{job.salary || 'Competitive'}</p>
                        </div>
                    </div>
                    <div className="bg-[#F6F7FB] rounded-2xl p-4 flex items-center gap-3">
                        <Clock className="w-5 h-5 text-gray-400" />
                        <div>
                            <p className="text-xs text-gray-400">Posted</p>
                            <p className="text-sm font-semibold">2 days ago</p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-4">
                    <div className="flex flex-wrap gap-2">
                        {getTags(job.title).map((tag) => (
                            <span
                                key={`${job.id}-${tag}`}
                                className="px-3 py-1 rounded-full border border-gray-200 text-xs text-gray-600 bg-gray-50"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>

                    {showStartButton && (
                        <button
                            type="button"
                            onClick={onStartInterview}
                            className="btn-orange px-6 py-2.5 text-sm rounded-2xl shadow-lg shadow-orange-500/20 shrink-0"
                        >
                            Start AI Interview
                        </button>
                    )}
                </div>
            </section>

            <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-orange-500" />
                    <h2 className="text-lg font-semibold">About the Role</h2>
                </div>
                {parsing ? (
                    <p className="text-gray-500 text-sm">Loading full description...</p>
                ) : fullDescription ? (
                    renderDescription(fullDescription)
                ) : job.description ? (
                    renderDescription(job.description)
                ) : (
                    <p className="text-gray-600 text-sm">
                        This role combines strategic ownership with cross-functional collaboration.
                    </p>
                )}
            </section>

            {showResponsibilities && (
            <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                    <ClipboardCheck className="w-5 h-5 text-orange-500" />
                    <h2 className="text-lg font-semibold">Responsibilities</h2>
                </div>
                <ul className="space-y-3 text-sm text-gray-600">
                    {parsing ? (
                        <p className="text-gray-500 text-sm">Loading full description...</p>
                    ) : responsibilities.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                            <span className="text-orange-500 mt-1">✓</span>
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            </section>
            )}

            {showRequirements && (
            <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-500" />
                    <h2 className="text-lg font-semibold">Requirements</h2>
                </div>
                <ul className="space-y-3 text-sm text-gray-600">
                    {parsing ? (
                        <p className="text-gray-500 text-sm">Loading full description...</p>
                    ) : requirements.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                            <span className="text-orange-500 mt-1">✓</span>
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            </section>
            )}

            {showBenefits && (
            <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-orange-500" />
                    <h2 className="text-lg font-semibold">Benefits & Perks</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600">
                    {parsing ? (
                        <p className="text-gray-500 text-sm col-span-full">Loading full description...</p>
                    ) : benefits.map((item) => (
                        <div key={item} className="bg-[#F6F7FB] rounded-2xl px-4 py-3">
                            {item}
                        </div>
                    ))}
                </div>
            </section>
            )}

            <section className="bg-[#1E293B] rounded-[28px] p-6 md:p-8 text-white shadow-md">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-orange-500 text-white flex items-center justify-center font-semibold">
                        {(job.company || 'C')[0].toUpperCase()}
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold">{job.company || 'Company'}</h3>
                        <p className="text-sm text-blue-100">Arts & Culture · Non-profit</p>
                    </div>
                </div>
                <p className="text-sm text-blue-100 mt-4">
                    The National Gallery of Art is one of the world’s leading museums, with a
                    collection spanning centuries of artistic achievement.
                </p>
                <div className="mt-6 grid grid-cols-3 gap-6 text-center">
                    <div>
                        <p className="text-2xl font-semibold text-orange-400">500+</p>
                        <p className="text-xs text-blue-100">Employees</p>
                    </div>
                    <div>
                        <p className="text-2xl font-semibold text-orange-400">4M+</p>
                        <p className="text-xs text-blue-100">Annual visitors</p>
                    </div>
                    <div>
                        <p className="text-2xl font-semibold text-orange-400">3</p>
                        <p className="text-xs text-blue-100">Open positions</p>
                    </div>
                </div>
                <div className="mt-6 flex items-center gap-2 text-xs text-blue-100">
                    <Building2 className="w-4 h-4" />
                    <span>Company profile</span>
                </div>
            </section>
            </div>
        </div>
    );
}
